import api from "../api/api";

export interface PolicyChatMessage {
  role: "user" | "assistant";
  content: string;
  created_at?: string;
}

export interface ChatStreamEvent {
  event: "thinking" | "token" | "done" | "error" | "references";
  data: string;
}

/** A policy file + page that grounded a chat answer. */
export interface SourceReference {
  policy: string
  /** Full source label, e.g. "Credit Policy — latest version, 2025, p. 3" */
  policy_reference: string
  document_id: number | null
  document_name: string
  page: string
}

export interface ChatSession {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_preview: string;
}

// ─── Chat Sessions ────────────────────────────────────────────────────────────

export async function getChatSessions(): Promise<ChatSession[]> {
  const res = await api.get("/api/v1/policy-search/chat/sessions");
  return Array.isArray(res.data) ? res.data : res.data?.data ?? [];
}

export async function createChatSession(): Promise<ChatSession> {
  const res = await api.post("/api/v1/policy-search/chat/sessions");
  return res.data;
}

export async function deleteChatSession(sessionId: number): Promise<void> {
  await api.delete(`/api/v1/policy-search/chat/sessions/${sessionId}`);
}

export async function getSessionMessages(sessionId: number): Promise<PolicyChatMessage[]> {
  const res = await api.get(`/api/v1/policy-search/chat/sessions/${sessionId}`);
  return Array.isArray(res.data) ? res.data : res.data?.data ?? [];
}

// ─── Chat ────────────────────────────────────────────────────────────────────

export async function getChatHistory(sessionId?: number): Promise<PolicyChatMessage[]> {
  const url = sessionId
    ? `/api/v1/policy-search/chat/history?session_id=${sessionId}`
    : "/api/v1/policy-search/chat/history";
  const res = await api.get(url);
  return Array.isArray(res.data) ? res.data : res.data?.data ?? [];
}

/**
 * Stream a chat answer via SSE. Calls onEvent with parsed SSE events:
 *   { event: 'thinking' | 'token' | 'done' | 'error', data: string }
 *
 * Uses fetch + ReadableStream (not axios) because axios doesn't support
 * streaming responses in the browser. The JWT access token is read from
 * localStorage to match the axios setup in api/api.ts.
 */
export async function streamPolicyChat(
  message: string,
  onEvent: (event: ChatStreamEvent) => void,
  sessionId?: number
): Promise<void> {
  const token = localStorage.getItem("access_token");
  const baseURL = (import.meta as any).env?.VITE_API_BASE_URL || "";
  const url = `${baseURL}/api/v1/policy-search/chat/stream`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, session_id: sessionId }),
  });

  if (!res.ok || !res.body) {
    throw new Error("Chat stream failed");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let idx
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx)
      buffer = buffer.slice(idx + 2)
      const event = parseSSEFrame(frame)
      if (event) onEvent(event)
    }
  }
}

function parseSSEFrame(frame: string): ChatStreamEvent | null {
  let event = "message"
  let dataLines: string[] = []
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim()
    } else if (line.startsWith("data:")) {
      // Per the SSE spec, the data is everything after "data:" and one
      // optional leading space. We must NOT trim — leading spaces are
      // significant (they are the spaces between streamed words/tokens).
      const raw = line.slice(5)
      dataLines.push(raw.startsWith(" ") ? raw.slice(1) : raw)
    }
  }
  // Join multiple data: lines with a newline (SSE spec), then un-escape
  // the newlines the server escaped for SSE transport.
  const data = dataLines.join("\n").replace(/\\n/g, "\n")
  return { event, data } as ChatStreamEvent
}

// ─── Compliance check ──────────────────────────────────────────────────────

export interface ComplianceViolation {
  policy: string
  /** Full source reference, e.g. "Credit Policy — latest version, 2025, p. 3" */
  policy_reference: string
  /** Backend PolicyDocument id — null when the source can't be resolved */
  document_id: number | null
  /** Original file name of the referenced policy document */
  document_name: string
  /** Page reference inside the policy document, e.g. "1-4" */
  page: string
  rule: string
  document_excerpt: string
  section: string
  explanation: string
}

export interface ComplianceResult {
  file: string
  /** true = compliant, false = violations found, null = corpus empty */
  compliant: boolean | null
  summary: string
  violations: ComplianceViolation[]
  /** Suggested follow-up questions the user can ask in chat */
  suggested_questions: string[]
  /** Policy names consulted during the check */
  policies_checked: string[]
  sections_checked: number
  truncated: boolean
}

/** Upload a document and check it against the policy corpus. */
export async function checkCompliance(file: File): Promise<ComplianceResult> {
  const form = new FormData()
  form.append("file", file)
  const res = await api.post(
    "/api/v1/policy-search/compliance-check",
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  )
  return res.data?.data ?? res.data
}

/** Download the raw file of a policy document (triggers a browser save). */
export async function downloadPolicyDocument(
  documentId: number,
  fileName: string
): Promise<void> {
  const res = await api.get(
    `/api/v1/policy-search/documents/${documentId}/download`,
    { responseType: "blob" }
  )
  const url = URL.createObjectURL(res.data)
  const a = document.createElement("a")
  a.href = url
  a.download = fileName || `policy-document-${documentId}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
